/**
 * Canonical hydration goal resolvers shared by client and server.
 * Formulas match client/src/lib/goalCalculations.ts (16-hour proration base).
 */

export type ProfileGoals = {
  dailyTarget?: number | null;
  proratedDailyTarget?: number | null;
  activeHoursStart?: string | null;
  activeHoursEnd?: string | null;
  reminderInterval?: number | null;
  age?: number | null;
  gender?: string | null;
  volumePerReminder?: number | null;
};

export function toProfileGoals(
  profile?: {
    dailyTarget?: number | null;
    proratedDailyTarget?: number | null;
    activeHoursStart?: string | null;
    activeHoursEnd?: string | null;
    reminderInterval?: number | null;
    age?: number | null;
    gender?: string | null;
    volumePerReminder?: number | null;
  } | null
): ProfileGoals {
  if (!profile) return {};
  return {
    dailyTarget: profile.dailyTarget ?? undefined,
    proratedDailyTarget: profile.proratedDailyTarget ?? undefined,
    activeHoursStart: profile.activeHoursStart ?? undefined,
    activeHoursEnd: profile.activeHoursEnd ?? undefined,
    reminderInterval: profile.reminderInterval ?? undefined,
    age: profile.age ?? undefined,
    gender: profile.gender ?? undefined,
    volumePerReminder: profile.volumePerReminder ?? undefined,
  };
}

export function parseTimeToDecimal(timeStr: string): number {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours + minutes / 60;
}

export function calculateActiveHours(startTime: string, endTime: string): number {
  const start = parseTimeToDecimal(startTime);
  const end = parseTimeToDecimal(endTime);
  if (end <= start) {
    return 24 - start + end;
  }
  return end - start;
}

export function calculateBaseDailyRecommendation(
  age?: number,
  gender?: string,
  activityLevel: 'low' | 'moderate' | 'high' = 'moderate'
): number {
  // Institute of Medicine daily fluid recommendations (water): male 3.7L, female 2.7L
  let baseRecommendation = 3200;

  if (gender === 'male') {
    baseRecommendation = 3700;
  } else if (gender === 'female') {
    baseRecommendation = 2700;
  }

  if (age) {
    if (age >= 65) {
      baseRecommendation *= 0.9;
    } else if (age >= 19) {
      baseRecommendation *= 1.0;
    } else if (age >= 14) {
      baseRecommendation *= 0.85;
    }
  }

  switch (activityLevel) {
    case 'high':
      baseRecommendation *= 1.15;
      break;
    case 'moderate':
      baseRecommendation *= 1.0;
      break;
    case 'low':
      baseRecommendation *= 0.95;
      break;
  }

  const rounded = Math.round(baseRecommendation / 100) * 100;
  return Math.max(2000, Math.min(5000, rounded));
}

export function calculateProratedTarget(
  baseDailyRecommendation: number = 3500,
  activeHours: number
): number {
  const proratedTarget = Math.round((baseDailyRecommendation / 16) * activeHours);
  return Math.max(1000, Math.min(5000, proratedTarget));
}

export function calculateRemindersPerDay(activeHours: number, reminderInterval: number): number {
  if (reminderInterval <= 0) return 0;
  const activeMinutes = Math.round(activeHours * 60);
  return Math.floor(activeMinutes / reminderInterval);
}

export function calculateVolumePerReminder(
  proratedGoal: number,
  activeHours: number,
  reminderInterval: number
): number {
  const totalReminders = (activeHours * 60) / reminderInterval;
  if (totalReminders === 0) return proratedGoal;
  const volumePerReminder = Math.round(proratedGoal / totalReminders);
  return Math.max(50, Math.min(1000, volumePerReminder));
}

export type HydrationPlan = {
  proratedDailyTarget: number;
  volumePerReminder: number;
  totalReminders: number;
  activeHours: number;
};

/** Derive all current hydration goals from profile fields (single source of truth). */
export function computeHydrationPlan(profile?: ProfileGoals | null): HydrationPlan {
  const start = profile?.activeHoursStart || '09:00';
  const end = profile?.activeHoursEnd || '18:00';
  const interval = profile?.reminderInterval || 45;
  const activeHours = calculateActiveHours(start, end);

  let proratedDailyTarget: number;
  if (profile?.dailyTarget && profile.dailyTarget > 0) {
    // User-set goal from edit profile / settings (ml for active hours)
    proratedDailyTarget = profile.dailyTarget;
  } else if (profile?.proratedDailyTarget && profile.proratedDailyTarget > 0) {
    proratedDailyTarget = profile.proratedDailyTarget;
  } else if (profile?.age != null || profile?.gender) {
    const base = calculateBaseDailyRecommendation(
      profile?.age ?? undefined,
      profile?.gender ?? undefined,
      'moderate'
    );
    proratedDailyTarget = calculateProratedTarget(base, activeHours);
  } else {
    proratedDailyTarget = 2500;
  }

  const totalReminders = calculateRemindersPerDay(activeHours, interval);
  const volumePerReminder = calculateVolumePerReminder(proratedDailyTarget, activeHours, interval);

  return { proratedDailyTarget, volumePerReminder, totalReminders, activeHours };
}

/** Keep stored profile hydration fields aligned after edits. */
export function syncProfileHydrationFields(profile?: ProfileGoals | null): {
  dailyTarget: number;
  proratedDailyTarget: number;
  volumePerReminder: number;
} {
  const plan = computeHydrationPlan(profile);
  return {
    dailyTarget: plan.proratedDailyTarget,
    proratedDailyTarget: plan.proratedDailyTarget,
    volumePerReminder: plan.volumePerReminder,
  };
}

/** Prorated daily volume goal (ml) from profile fields. */
export function resolveProratedDailyTargetMl(profile?: ProfileGoals | null): number {
  return computeHydrationPlan(profile).proratedDailyTarget;
}

/** Single source of truth for daily volume goal (ml) across dial, recap, profile, stats. */
export function resolveDailyTargetMl(profile?: ProfileGoals | null): number {
  return resolveProratedDailyTargetMl(profile);
}

export function resolveGoalLitres(profile?: ProfileGoals | null): number {
  return resolveDailyTargetMl(profile) / 1000;
}

/** Target ml per reminder derived from current profile goals. */
export function resolveVolumePerReminder(profile?: ProfileGoals | null): number {
  return computeHydrationPlan(profile).volumePerReminder;
}

/** Expected sips (reminders) per active day. */
export function resolveFrequencyGoal(profile?: ProfileGoals | null): number {
  return computeHydrationPlan(profile).totalReminders;
}

/** Format onboarding baseline drinking frequency (stored in minutes). */
export function formatBaselineReminderInterval(minutes: number): string {
  if (minutes === 60) return 'Every hour';
  if (minutes === 90) return 'Every 90 minutes';
  if (minutes === 180) return 'Every 3 hours';
  if (minutes === 240) return 'Every 4 hours';
  return `Every ${minutes} minutes`;
}
